// ReportViz — shared, dependency-free (pure CSS/SVG) building blocks for the modernized
// survey reports (Analytical / Summary / Comparation). No chart library: everything here is
// plain React + inline styles, so it always builds on the old CRA/webpack toolchain.
//
// Data contract: these helpers consume the `reports` array produced by
// Score.getReportOfQuestion(q, responses) — an ordered list of buckets
// [{ answer, answerLabel, count, percent }] plus a numeric questionScore.
//
// Rating questions (type === 'radio') are treated as an ordered scale: segments are coloured by
// their ANSWER VALUE (warm → green) and displayed worst→best, so a reversed-scale survey
// (e.g. the Aquila 2026 clones where 5 = best) still renders correctly. Other question types
// fall back to a neutral categorical palette in their natural option order.

import { default as React } from 'react';
import { t } from 'i18next';

const MONO = '"SF Mono",ui-monospace,Menlo,Consolas,monospace';

// warm (low / poor) → green (high / excellent), same anchors as the approved mockup
const ANCHORS = [[196, 69, 54], [233, 162, 59], [167, 201, 87], [99, 149, 74], [47, 107, 79]];
// neutral categorical palette for non-rating questions (yes/no, checkbox, number, …)
const CAT_PALETTE = ['#3b6fd4', '#2f9e44', '#e9a23b', '#8e6bd4', '#0aa5b5', '#d1495b', '#6b7b8c', '#c98a2b'];

function isRating(question) {
    return question && question.question && question.question.type === 'radio';
}

function totalN(reports) {
    return (reports || []).reduce((a, r) => a + (r.count || 0), 0);
}

// interpolate the warm→green ramp for a value within [minV, maxV]
function valueColor(v, minV, maxV) {
    if (maxV <= minV) return `rgb(${ANCHORS[2].join(',')})`;
    const p = (v - minV) / (maxV - minV) * (ANCHORS.length - 1);
    const k = Math.min(ANCHORS.length - 2, Math.floor(p));
    const f = p - k;
    const c = ANCHORS[k].map((x, j) => Math.round(x + (ANCHORS[k + 1][j] - x) * f));
    return `rgb(${c.join(',')})`;
}

// Normalize a question's reports into display buckets.
// Rating → sorted worst→best, coloured by value, carries min/max value (= the scale).
// Categorical → natural order, categorical palette.
function ratingBuckets(question, reports) {
    const list = reports || [];
    const total = totalN(list);
    if (isRating(question)) {
        const withVal = list
            .map(r => ({ answer: r.answer, label: r.answerLabel, count: r.count || 0, value: Number(r.answer) }))
            .filter(r => !isNaN(r.value));
        const vals = withVal.map(r => r.value);
        const minV = vals.length ? Math.min.apply(null, vals) : 0;
        const maxV = vals.length ? Math.max.apply(null, vals) : 0;
        const sorted = withVal.slice().sort((a, b) => a.value - b.value);
        return {
            total, rating: true, minV, maxV,
            buckets: sorted.map(r => ({
                key: String(r.answer), label: r.label, value: r.value, count: r.count,
                pct: total ? r.count / total * 100 : 0,
                color: valueColor(r.value, minV, maxV),
            })),
        };
    }
    return {
        total, rating: false, minV: 0, maxV: 0,
        buckets: list.map((r, i) => ({
            key: String(r.answer) + '-' + i, label: r.answerLabel, value: null, count: r.count || 0,
            pct: total ? (r.count || 0) / total * 100 : 0,
            color: CAT_PALETTE[i % CAT_PALETTE.length],
        })),
    };
}

// Focus a report's sections on the filter's selected section (and question, if any) so the
// thing the user filtered for is the ONLY thing shown — no scrolling to a section buried deep
// in the survey. Section selected → keep only that section. Question also selected → within it
// keep only that question (matched by id, so skipped question types before it don't shift the
// target). Nothing selected → sections unchanged. `indices[i]` carries each kept section's
// ORIGINAL position so a compare period (period B) can be looked up at the same index.
function focusSelection(reportSections, surveyQuestion, filterData) {
    const list = reportSections || [];
    const out = { sections: list, indices: list.map((_, i) => i), focused: false, sectionTitle: '', questionTitle: '' };
    if (!filterData || !filterData.section) return out;
    const secIdx = filterData.section.value;
    const sec = list[secIdx];
    if (!sec) return out;
    out.focused = true;
    out.sectionTitle = sec.sectionTitle;
    out.indices = [secIdx];
    let section = sec;
    if (filterData.question) {
        const qIdx = filterData.question.value;
        const srcQ = surveyQuestion && surveyQuestion.sections[secIdx] && surveyQuestion.sections[secIdx].questions[qIdx];
        const qid = srcQ ? srcQ.id : null;
        const only = qid != null ? sec.reportQuestions.filter(rq => rq.q && rq.q.id === qid) : [];
        if (only.length) {
            section = Object.assign({}, sec, { reportQuestions: only });
            out.questionTitle = only[0].questionTitle;
        }
    }
    out.sections = [section];
    return out;
}

// % of answers at or above the middle of the scale (rating questions only). null otherwise.
function positivePercent(question, reports) {
    const b = ratingBuckets(question, reports);
    if (!b.rating || !b.total) return null;
    const thr = Math.ceil((b.minV + b.maxV) / 2);
    const pos = b.buckets.filter(x => x.value >= thr).reduce((a, x) => a + x.count, 0);
    return Math.round(pos / b.total * 100);
}

// ---- presentational components (inline styles, light theme) ----

function ScoreChip({ score, scale }) {
    return (
        <span style={{
            fontFamily: MONO, fontWeight: 700, fontSize: 13, padding: '3px 9px', borderRadius: 99,
            background: '#e7f4ea', color: '#2f8a45', whiteSpace: 'nowrap',
        }}>
            {Number(score || 0).toFixed(2)}{scale ? ' / ' + scale : ''}
        </span>
    );
}

// A/B badge used in comparison rows
function Pill({ ab }) {
    return (
        <span style={{
            fontSize: 10.5, fontWeight: 800, width: 20, height: 20, flex: 'none', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', borderRadius: 6, color: '#fff',
            background: ab === 'B' ? '#3b6fd4' : '#2f9e44',
        }}>{ab}</span>
    );
}

// The diverging/stacked distribution bar. Segment label shows "count (pct%)" when wide,
// just the count when medium, nothing when narrow — the full label sits in the tooltip.
function DistributionBar({ question, reports, height }) {
    const b = ratingBuckets(question, reports);
    const h = height || 24;
    if (!b.total) {
        return <div style={{ fontSize: 12, color: '#8a988f', padding: '4px 0' }}>{t('No answers')}</div>;
    }
    return (
        <div style={{
            position: 'relative', display: 'flex', height: h, borderRadius: 6, overflow: 'hidden',
            background: '#f0f3f1', gap: 2,
        }}>
            {b.buckets.filter(x => x.count > 0).map(x => {
                const lab = x.pct >= 14 ? `${x.count} (${Math.round(x.pct)}%)` : (x.pct >= 7 ? `${x.count}` : '');
                return (
                    <div key={x.key}
                        title={`${x.label}: ${x.count} (${x.pct.toFixed(0)}%)`}
                        style={{
                            width: x.pct + '%', background: x.color, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', minWidth: 0,
                            overflow: 'hidden', whiteSpace: 'nowrap', textShadow: '0 1px 1px rgba(0,0,0,.25)',
                        }}>{lab}</div>
                );
            })}
        </div>
    );
}

// Legend of a question's buckets (labels + colours), worst→best. Discrete swatches for small
// scales / categorical questions; a compact warm→green gradient strip for wide rating scales
// (e.g. 1–10) where ten swatches would be noise.
function RatingLegend({ question, reports }) {
    const b = ratingBuckets(question, reports);
    if (!b.buckets.length) return null;
    if (b.rating && b.buckets.length > 6) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 2px 2px', fontSize: 12, color: '#8a988f' }}>
                <span>{b.minV}</span>
                <span style={{
                    flex: 1, height: 8, borderRadius: 4, minWidth: 90,
                    background: `linear-gradient(90deg,${valueColor(b.minV, b.minV, b.maxV)},${valueColor((b.minV + b.maxV) / 2, b.minV, b.maxV)},${valueColor(b.maxV, b.minV, b.maxV)})`,
                }} />
                <span>{b.maxV}</span>
            </div>
        );
    }
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: '#5d6d66', margin: '7px 2px 2px' }}>
            {b.buckets.map(x => (
                <span key={x.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <i style={{ width: 11, height: 11, borderRadius: 3, background: x.color, display: 'inline-block' }} />{x.label}
                </span>
            ))}
        </div>
    );
}

// Two stacked distribution bars, period A over period B, each tagged with an A/B pill.
function CompareBars({ question, reportsA, reportsB }) {
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <Pill ab="A" /><div style={{ flex: 1 }}><DistributionBar question={question} reports={reportsA} /></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <Pill ab="B" /><div style={{ flex: 1 }}><DistributionBar question={question} reports={reportsB} /></div>
            </div>
        </div>
    );
}

// Thin score meter (0..max) with a warm→green gradient fill.
function Meter({ value, max, height }) {
    const pct = max ? Math.max(0, Math.min(100, value / max * 100)) : 0;
    return (
        <div style={{ height: height || 9, borderRadius: 99, background: '#e2e8e4', overflow: 'hidden' }}>
            <div style={{ width: pct + '%', height: '100%', borderRadius: 99, background: 'linear-gradient(90deg,#e9a23b,#2f9e44)' }} />
        </div>
    );
}

// Δ = a − b, coloured up/down with an arrow. Returns a JSX span.
function deltaText(a, b) {
    const d = (a || 0) - (b || 0);
    const up = d >= 0;
    return (
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, marginLeft: 6, color: up ? '#2f9e44' : '#d1495b' }}>
            {up ? '▲' : '▼'} {up ? '+' : ''}{d.toFixed(2)}
        </span>
    );
}

// KPI card (overall score, responses, positive%, …) for the report header strip.
function KpiCard({ label, value, unit, delta, meterPct }) {
    return (
        <div style={{
            background: '#fff', border: '1px solid #e2e8e4', borderRadius: 14, padding: '16px 18px',
            boxShadow: '0 1px 2px rgba(20,40,30,.06),0 6px 20px rgba(20,40,30,.05)',
            height: '100%', boxSizing: 'border-box',
        }}>
            <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.08em', color: '#8a988f', fontWeight: 700 }}>{label}</div>
            <div style={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, marginTop: 6, letterSpacing: '-.02em', color: '#1b2320' }}>
                {value}{unit ? <span style={{ fontSize: 15, color: '#8a988f', fontWeight: 600 }}>{unit}</span> : null}
            </div>
            {delta != null ? delta : null}
            {meterPct != null ? <div style={{ marginTop: 10 }}><Meter value={meterPct} max={100} height={7} /></div> : null}
        </div>
    );
}

export {
    MONO,
    isRating,
    totalN,
    valueColor,
    ratingBuckets,
    focusSelection,
    positivePercent,
    ScoreChip,
    Pill,
    DistributionBar,
    RatingLegend,
    CompareBars,
    Meter,
    deltaText,
    KpiCard,
};
